// src/app/core/http/cache-store.service.ts
import { Injectable } from '@angular/core';

interface CacheEntry {
  data:      unknown;
  expiresAt: number;
}

/**
 * CacheStoreService — almacén en memoria para el CacheInterceptor.
 *
 * Los interceptors funcionales no pueden tener estado propio.
 * Este servicio actúa como singleton que el interceptor inyecta.
 *
 * La caché es por sesión — se destruye al hacer logout (invalidateAll).
 * No persiste en sessionStorage ni localStorage.
 */
@Injectable({ providedIn: 'root' })
export class CacheStoreService {
  private readonly store = new Map<string, CacheEntry>();

  /** Obtiene una entrada cacheada si existe y no ha expirado */
  get(key: string): unknown | null {
    const entry = this.store.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return null;
    }
    return entry.data;
  }

  /** Almacena una respuesta con TTL en milisegundos */
  set(key: string, data: unknown, ttlMs: number): void {
    this.store.set(key, {
      data,
      expiresAt: Date.now() + ttlMs,
    });
  }

  /** Elimina una entrada específica */
  delete(key: string): void {
    this.store.delete(key);
  }

  /**
   * Invalida toda la caché.
   * Llamado al hacer logout para evitar que datos de un usuario
   * sean visibles para otro en la misma máquina.
   */
  invalidateAll(): void {
    this.store.clear();
  }

  /** Número de entradas activas (para debugging) */
  get size(): number {
    return this.store.size;
  }
}
